import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/apiClient";
import { Search, Loader2, Pill } from "lucide-react";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUS_STYLE = {
  issued: "bg-blue-50 text-blue-700", draft: "bg-slate-100 text-slate-600", received: "bg-amber-50 text-amber-700",
  processing: "bg-amber-50 text-amber-700", ready: "bg-emerald-50 text-emerald-700", dispensed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-red-50 text-red-600", expired: "bg-red-50 text-red-600",
};
const STATUSES = ["draft", "issued", "received", "processing", "ready", "dispensed", "cancelled", "expired"];

export default function PrescriptionHistory() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [pharmacy, setPharmacy] = useState("all");
  const [status, setStatus] = useState("all");

  const load = () => {
    setLoading(true);
    const q = {};
    if (search) q.search = search;
    if (pharmacy !== "all") q.pharmacy = pharmacy;
    if (status !== "all") q.status = status;
    api.get("/prescriptions", { params: q }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { api.get("/pharmacies").then((r) => setPharmacies(r.data)); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search, pharmacy, status]);

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-head">Prescription History</h1>
      <p className="text-slate-500 mt-1">Search, filter and reuse past prescriptions.</p>
      <div className="flex flex-wrap gap-3 mt-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="rxh-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Patient name or Rx number…" className="pl-10 h-12 rounded-xl" />
        </div>
        <Select value={pharmacy} onValueChange={setPharmacy}>
          <SelectTrigger data-testid="rxh-filter-pharmacy" className="h-12 rounded-xl w-full sm:w-56"><SelectValue placeholder="Pharmacy" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All pharmacies</SelectItem>{pharmacies.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="rxh-filter-status" className="h-12 rounded-xl w-full sm:w-44"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mt-6 overflow-hidden">
        {loading ? <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div> :
          items.length === 0 ? <div className="p-16 text-center text-slate-400"><Pill className="w-10 h-10 mx-auto mb-3" /><p>No prescriptions match your filters.</p></div> :
            <div className="divide-y divide-slate-100">
              {items.map((r) => (
                <button key={r.id} data-testid={`rxh-row-${r.id}`} onClick={() => navigate(`/prescriptions/${r.id}`)} className="w-full flex items-center justify-between p-4 hover:bg-slate-50 text-left">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{r.patient?.first_name} {r.patient?.last_name}</p>
                    <p className="text-sm text-slate-500 truncate">{r.medications?.map((m) => m.name).join(", ")}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{r.pharmacy_snapshot?.name || (r.patient_chooses ? "Patient chooses" : "No pharmacy")} · {r.prescription_number}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_STYLE[r.status]}`}>{r.status}</span>
                    <span className="text-xs text-slate-400 hidden sm:block">{r.created_at?.slice(0, 10)}</span>
                  </div>
                </button>
              ))}
            </div>}
      </div>
    </div>
  );
}
